import { useEffect, useState } from 'react';
import { DownloadPDFWrapperProps } from '@msc-b2c/interfaces/types';
import { loadDownloadPdfWrapper } from './loadDownloadPdfWrapper';
import type DownloadPDFWrapperComponent from './components/DownloadPDFWrapper';

/**
 * AEM-registered entry point for the "download cruise PDF" feature.
 *
 * The actual implementation (and its ~2.3 MB `@react-pdf/renderer` stack)
 * lives in a separate, independently-built bundle (`DownloadPDFWrapper.js`,
 * see `apps/my-msc/vite.downloadPdfChunk.config.ts`) that is fetched on
 * demand via a plain `<script>` tag rather than a Rollup dynamic import --
 * see `loadDownloadPdfWrapper.ts` for why (`React.lazy()`/native
 * `import()` are NOT usable here; this repo's main build strips ESM
 * import/export syntax from every chunk via `rollup-plugin-iife`, which
 * breaks native code-splitting -- see further-optimizations.md
 * "CRITICAL FIX").
 *
 * `aemResourceType` is a plain static string here (not re-derived from the
 * lazy-loaded module), so the AEM component registry can look this wrapper
 * up synchronously at registration time, before the chunk has loaded.
 */
export function DownloadPDFWrapperLazy(props: Readonly<DownloadPDFWrapperProps>) {
  const [Component, setComponent] = useState<typeof DownloadPDFWrapperComponent | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadDownloadPdfWrapper()
      .then((LoadedComponent) => {
        if (!cancelled) {
          setComponent(() => LoadedComponent);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[DownloadPDFWrapperLazy] failed to load DownloadPDFWrapper chunk', error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!Component) {
    return null;
  }

  return <Component {...props} />;
}

DownloadPDFWrapperLazy.aemResourceType = 'b2c-website-foundation/components/mymsc/downloadpdf';

export default DownloadPDFWrapperLazy;
